import React, { useRef, useState, useLayoutEffect } from 'react';
import { motion, useReducedMotion, AnimatePresence } from 'framer-motion';
import { Calendar, Leaf, Activity, Heart, BookOpen, Coffee, Moon } from 'lucide-react';
import { springConfig, staggerContainer, staggerChild } from '../../utils/animations';

interface ViewOption {
  name: string;
  label: string;
  icon: React.ComponentType<{ size?: number }>;
}

interface ViewSelectorProps {
  currentView: string;
  onViewChange: (view: string) => void;
}

const viewOptions: ViewOption[] = [
  { name: "weeks", label: "Weeks", icon: Calendar },
  { name: "years", label: "Years", icon: Calendar },
  { name: "seasons", label: "Seasons", icon: Leaf },
  { name: "progress", label: "Progress", icon: Activity },
  { name: "calendar", label: "Months", icon: Calendar },
  { name: "book", label: "Life Book", icon: BookOpen },
  { name: "heartbeats", label: "Heartbeats", icon: Heart },
  { name: "daily", label: "Daily Life", icon: Coffee },
  { name: "cosmic", label: "Cosmic View", icon: Moon }
];

const ViewSelector: React.FC<ViewSelectorProps> = ({ currentView, onViewChange }) => {
  const prefersReducedMotion = useReducedMotion();
  const containerRef = useRef<HTMLDivElement>(null);
  const buttonRefs = useRef<Map<string, HTMLButtonElement>>(new Map());
  const [indicatorStyle, setIndicatorStyle] = useState({ left: 0, width: 0 });
  const [isInitialized, setIsInitialized] = useState(false);

  // Update indicator position when currentView changes
  useLayoutEffect(() => {
    const button = buttonRefs.current.get(currentView);
    const container = containerRef.current;

    if (button && container) {
      const containerRect = container.getBoundingClientRect();
      const buttonRect = button.getBoundingClientRect();

      setIndicatorStyle({
        left: buttonRect.left - containerRect.left,
        width: buttonRect.width
      });

      // Mark as initialized after first calculation
      if (!isInitialized) {
        setTimeout(() => setIsInitialized(true), 50);
      }
    }
  }, [currentView, isInitialized]);

  // Tab button variants
  const buttonVariants = {
    rest: { scale: 1 },
    hover: { scale: 1.05 },
    tap: { scale: 0.95 }
  };

  // Icon animation variants
  const iconVariants = {
    rest: { rotate: 0 },
    hover: { rotate: [0, -10, 10, 0] },
    active: {
      scale: [1, 1.2, 1],
      transition: { duration: 0.3 }
    }
  };

  // If reduced motion is preferred, render simpler version
  if (prefersReducedMotion) {
    return (
      <div className="flex flex-wrap justify-center gap-3 mb-8">
        {viewOptions.map((option) => {
          const isActive = currentView === option.name;
          return (
            <button
              key={option.name}
              onClick={() => onViewChange(option.name)}
              className={`
                inline-flex items-center justify-center px-3 py-1 text-sm font-medium rounded-full
                transition-colors duration-200
                ${isActive
                  ? 'bg-blue-500 dark:bg-catppuccin-blue text-white'
                  : 'bg-gray-100 dark:bg-catppuccin-surface1 text-gray-700 dark:text-catppuccin-subtext0 hover:bg-gray-200 dark:hover:bg-catppuccin-surface2'
                }
              `}
            >
              <option.icon size={16} />
              <span className="ml-2">{option.label}</span>
            </button>
          );
        })}
      </div>
    );
  }

  return (
    <motion.div
      ref={containerRef}
      className="relative flex flex-wrap justify-center gap-3 mb-8"
      variants={staggerContainer(0.05, 0.1)}
      initial="hidden"
      animate="visible"
    >
      {/* Sliding background indicator */}
      <AnimatePresence>
        {isInitialized && (
          <motion.div
            className="absolute top-0 h-full rounded-full bg-blue-500 dark:bg-catppuccin-blue -z-10"
            initial={false}
            animate={{
              left: indicatorStyle.left,
              width: indicatorStyle.width,
            }}
            transition={springConfig.snappy}
            style={{
              height: '100%',
              maxHeight: '34px'
            }}
            layoutId="activeTab"
          />
        )}
      </AnimatePresence>

      {viewOptions.map((option, index) => {
        const isActive = currentView === option.name;
        const Icon = option.icon;

        return (
          <motion.button
            key={option.name}
            ref={(el) => {
              if (el) buttonRefs.current.set(option.name, el);
            }}
            onClick={() => onViewChange(option.name)}
            className={`
              inline-flex items-center justify-center px-3 py-1 text-sm font-medium rounded-full
              transition-colors duration-200 relative z-10
              ${isActive
                ? 'text-white'
                : 'bg-gray-100 dark:bg-catppuccin-surface1 text-gray-700 dark:text-catppuccin-subtext0 hover:bg-gray-200 dark:hover:bg-catppuccin-surface2'
              }
            `}
            variants={{
              ...staggerChild,
              ...buttonVariants
            }}
            initial="hidden"
            animate="visible"
            whileHover="hover"
            whileTap="tap"
          >
            <motion.span
              className="flex items-center"
              variants={iconVariants}
              animate={isActive ? "active" : "rest"}
            >
              <Icon size={16} />
            </motion.span>
            <span className="ml-2">{option.label}</span>
          </motion.button>
        );
      })}
    </motion.div>
  );
};

export default ViewSelector;
